import {
  DataSourceDatabaseId,
  DataSourceSchemaId,
  DataSourceTableId,
  SemanticDatasetName,
  SemanticProjectFileId,
  SemanticProjectId,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { SemanticProjectProblem } from "../../components/semantic-project-workbench/problems/types";
import { SEMANTIC_PROJECT_WORKBENCH_RIGHT_SIDEBAR_PANEL_ID } from "../../components/semantic-project-workbench/sidebar/storage";
import {
  CloseWorkbenchTabPayload,
  FocusWorkbenchTabPayload,
  NewWorkbenchTabPayload,
  UpdateWorkbenchTabPayload,
  closeWorkbenchTab,
  focusWorkbenchTab,
  newWorkbenchTab,
  updateWorkbenchTab,
} from "../../components/workbench/redux-reducers/tabReducers";
import {
  UpdateWorkbenchViewportPayload,
  updateWorkbenchViewport,
} from "../../components/workbench/redux-reducers/viewportReducers";
import {
  WorkbenchTab,
  WorkbenchTabGroup,
  WorkbenchTabGroupId,
  WorkbenchViewport,
} from "../../components/workbench/state";
import {
  getStorageValue,
  setStorageValue,
} from "../../hooks/useBrowserStorage";
import { RootState } from "../store";

// *** State *****************************************************************
export type SemanticProjectWorkbenchState = {
  projectId: SemanticProjectId | null;
  lastProjectId: SemanticProjectId | null;
  tabGroups: WorkbenchTabGroup<SemanticProjectWorkbenchEditorTab>[];
  viewport: WorkbenchViewport;
  mainSidebar: { panelId: SemanticProjectWorkbenchSidebarPanelId | null };
  bottomSidebar: { panelId: SemanticProjectWorkbenchSidebarPanelId | null };
  rightSidebar: { panelId: SemanticProjectWorkbenchSidebarPanelId | null };
  problems: {
    /** problems scanned for on a regular schedule by client code, containing
     * simple problems we can catch without needing to run the build. */
    clientSide: SemanticProjectProblem[];
  };
};

export type SemanticProjectWorkbenchEditorTab =
  | SemanticProjectWorkbenchEditorTab.FileEditor
  | SemanticProjectWorkbenchEditorTab.FileDiffEditor
  | SemanticProjectWorkbenchEditorTab.NewDatasetFile;
export namespace SemanticProjectWorkbenchEditorTab {
  type _Base<TK extends string, X> = WorkbenchTab & { type: TK } & X;
  export type FileEditor = _Base<
    "fileEditor",
    { fileId: SemanticProjectFileId }
  >;
  export type FileDiffEditor = _Base<
    "fileDiffEditor",
    { fileId: SemanticProjectFileId; view: "inline" | "side-by-side" }
  >;
  export type NewDatasetFile = _Base<
    "newDatasetFile",
    { formState: SemanticProjectWorkbenchNewDatasetFileData }
  >;
}

export type SemanticProjectWorkbenchNewDatasetFileData = {
  name: SemanticDatasetName;
  description: string;
  base:
    | {
        type: "table";
        structuredTable: {
          table: { id: DataSourceTableId; name: string };
          schema: { id: DataSourceSchemaId; name: string };
          database: { id: DataSourceDatabaseId; name: string | null } | null;
        } | null;
      }
    | { type: "sql"; sql: string }
    | null;
  import: {
    columnsAsDimensions: boolean;
    columnDescriptions: boolean;
  };
};
export namespace SemanticProjectWorkbenchNewDatasetFileData {
  export const EMPTY: SemanticProjectWorkbenchNewDatasetFileData = {
    name: SemanticDatasetName.check(""),
    description: "",
    base: null,
    import: { columnsAsDimensions: true, columnDescriptions: true },
  };
}

export type SemanticProjectWorkbenchSidebarPanelId =
  | "files"
  | "dataBrowser"
  | "help"
  | "problems"
  | "chat"
  | "history"
  // deprecated, but may be present in storage
  | "datasets";

const initialState: SemanticProjectWorkbenchState = {
  projectId: null,
  lastProjectId: null,
  mainSidebar: { panelId: "files" },
  bottomSidebar: { panelId: null },
  rightSidebar: { panelId: "chat" },
  tabGroups: [],
  viewport: { type: "empty" },
  problems: { clientSide: [] },
};

// *** Actions ****************************************************************
const semanticProjectWorkbenchSlice = createSlice({
  name: "semanticProjectWorkbench",
  initialState,
  reducers: {
    /**
     * Initialize the workbench state for a given project. Preserves existing
     * state if the project is the same.
     */
    init(
      state: SemanticProjectWorkbenchState,
      action: PayloadAction<{ projectId: SemanticProjectId }>,
    ) {
      if (state.lastProjectId !== action.payload.projectId) {
        // must return so redux has new reference
        const rightSidebarPanelId = getStorageValue(
          SEMANTIC_PROJECT_WORKBENCH_RIGHT_SIDEBAR_PANEL_ID({
            semanticProjectId: action.payload.projectId,
          }),
          "Local",
        );
        return {
          ...initialState,
          projectId: action.payload.projectId,
          lastProjectId: state.lastProjectId,
          rightSidebar: {
            ...initialState.rightSidebar,
            panelId: rightSidebarPanelId,
          },
        };
      }

      state.projectId = action.payload.projectId;
    },
    unmount(state: SemanticProjectWorkbenchState) {
      state.lastProjectId = state.projectId;
      state.projectId = null;
    },

    /** Change a sidebar's panel. */
    setSidebarPanel(
      state: SemanticProjectWorkbenchState,
      action: PayloadAction<{
        sidebar: "main" | "bottom" | "right";
        panelId: SemanticProjectWorkbenchSidebarPanelId | null;
        /** if true, will instead toggle the panel if the panel with that id is already open. */
        toggleIfOpen?: boolean;
      }>,
    ) {
      const sidebarKey = `${action.payload.sidebar}Sidebar` as const;
      let value: SemanticProjectWorkbenchSidebarPanelId | null;
      if (
        action.payload.toggleIfOpen &&
        state[sidebarKey].panelId === action.payload.panelId
      ) {
        value = null;
      } else {
        value = action.payload.panelId;
      }
      state[sidebarKey].panelId = value;

      if (state.projectId && sidebarKey === "rightSidebar") {
        setStorageValue(
          SEMANTIC_PROJECT_WORKBENCH_RIGHT_SIDEBAR_PANEL_ID({
            semanticProjectId: state.projectId,
          }),
          value,
          "Local",
        );
      }
    },

    /** Bindings for problem tracking */
    setClientSideProblems(
      state: SemanticProjectWorkbenchState,
      action: PayloadAction<{
        reporter: SemanticProjectProblem["reporter"];
        problems: SemanticProjectProblem[];
      }>,
    ) {
      if (
        !action.payload.problems.every(
          (p) => p.reporter === action.payload.reporter,
        )
      ) {
        throw new Error("Problems must all have the same reporter");
      }
      // remove all existing problems from this reporter
      state.problems.clientSide = state.problems.clientSide.filter(
        (p) => p.reporter !== action.payload.reporter,
      );
      // insert all the problems
      state.problems.clientSide.push(...action.payload.problems);
    },

    /* Bindings for editor tab, viewport APIs */
    newEditorTab(
      state: SemanticProjectWorkbenchState,
      action: PayloadAction<
        NewWorkbenchTabPayload<SemanticProjectWorkbenchEditorTab>
      >,
    ) {
      newWorkbenchTab(state, action.payload);
    },
    focusEditorTab(
      state: SemanticProjectWorkbenchState,
      action: PayloadAction<FocusWorkbenchTabPayload>,
    ) {
      focusWorkbenchTab(state, action.payload);
    },
    closeEditorTab(
      state: SemanticProjectWorkbenchState,
      action: PayloadAction<CloseWorkbenchTabPayload>,
    ) {
      closeWorkbenchTab(state, action.payload);
    },
    updateEditorTab(
      state: SemanticProjectWorkbenchState,
      action: PayloadAction<
        UpdateWorkbenchTabPayload<SemanticProjectWorkbenchEditorTab>
      >,
    ) {
      updateWorkbenchTab(state, action.payload);
    },
    updateEditorViewport(
      state: SemanticProjectWorkbenchState,
      action: PayloadAction<UpdateWorkbenchViewportPayload>,
    ) {
      updateWorkbenchViewport(state, action.payload);
    },
  },
});

export const semanticProjectWorkbenchReducer =
  semanticProjectWorkbenchSlice.reducer;

export const semanticProjectWorkbenchActions =
  semanticProjectWorkbenchSlice.actions;

// *** Selectors **************************************************************
export function selectSemanticProjectWorkbench(
  state: RootState,
): SemanticProjectWorkbenchState {
  return state.semanticProjectWorkbench;
}

export function selectSemanticProjectWorkbenchEditorPrimaryTabGroupId(
  state: RootState,
): WorkbenchTabGroupId | null {
  return state.semanticProjectWorkbench.tabGroups[0]?.id ?? null;
}
